import * as React from 'react';

import { PROJECT_MANAGEMENT_APP_ID } from '../../apps/appsRegistry';
import { pathInWorkspace, workspaceRootPath } from '../../layout/odooNavUtils';
import { subGet } from './projectSubApi';

/** In-workspace route paths for the project smart-button scoped lists (registered in ProjectManagementWorkspace). */
export const PROJECT_ISSUES_ROUTE = '/issues';
export const PROJECT_CHANGE_REQUESTS_ROUTE = '/change-requests';
export const PROJECT_TIMESHEETS_ROUTE = '/timesheets';
export const PROJECT_TIMESHEET_DAY_ROUTE = '/timesheet-day';
export const PROJECT_RISKS_ROUTE = '/risks';
// Payment Schedules is a full react-admin resource (Pay-Runs-style list + document form),
// not a custom scoped-list route — the path is the resource list, scoped via ?projectId=.
export const PROJECT_PAYMENT_SCHEDULES_ROUTE = '/projectPaymentSchedules';

/** Builds the absolute, cross-workspace URL for a scoped list, carrying the project scope + breadcrumb state. */
export function buildProjectScopedListPath(
    route: string,
    opts: { projectId: number; returnTo?: string; projectLabel?: string }
): string {
    const params = new URLSearchParams();
    params.set('projectId', String(opts.projectId));
    if (opts.returnTo) params.set('returnTo', opts.returnTo);
    if (opts.projectLabel) params.set('projectLabel', opts.projectLabel);
    return pathInWorkspace(workspaceRootPath(PROJECT_MANAGEMENT_APP_ID), `${route}?${params.toString()}`);
}

/**
 * Keeps only the project-scope params (projectId/projectLabel/returnTo) of a location.search.
 * react-admin syncs list state (filter/order/page/perPage) into the list URL — those params must
 * not leak into create/edit URLs, where they cause store churn and a visible flicker on save.
 */
export function scopedSearchOnly(search: string): string {
    const qs = new URLSearchParams(search);
    const out = new URLSearchParams();
    for (const key of ['projectId', 'projectLabel', 'returnTo']) {
        const v = qs.get(key);
        if (v) out.set(key, v);
    }
    const s = out.toString();
    return s ? `?${s}` : '';
}

/** Reads the scope (projectId) + breadcrumb bits a scoped list needs from its query string. */
export function useScopedListParams(search: string) {
    return React.useMemo(() => {
        const qs = new URLSearchParams(search);
        const projectId = Number(qs.get('projectId') ?? 0);
        const returnToRaw = qs.get('returnTo');
        let returnTo: string | undefined;
        if (returnToRaw) {
            try {
                returnTo = decodeURIComponent(returnToRaw);
            } catch {
                returnTo = returnToRaw;
            }
        }
        const labelRaw = qs.get('projectLabel');
        const projectLabel = labelRaw ? (() => { try { return decodeURIComponent(labelRaw); } catch { return labelRaw; } })() : '';
        return { projectId: Number.isFinite(projectId) && projectId > 0 ? projectId : 0, returnTo, projectLabel };
    }, [search]);
}

/** Fetches a project-scoped record list and exposes a reload trigger. */
export function useProjectScopedRecords<T>(endpoint: string, projectId: number): { rows: T[]; reload: () => void } {
    const [rows, setRows] = React.useState<T[]>([]);
    const [key, setKey] = React.useState(0);
    const reload = React.useCallback(() => setKey(k => k + 1), []);
    React.useEffect(() => {
        if (!projectId) {
            setRows([]);
            return;
        }
        let alive = true;
        void (async () => {
            const data = await subGet<T>(`${endpoint}?projectId=${projectId}`);
            if (alive) setRows(data);
        })();
        return () => {
            alive = false;
        };
    }, [endpoint, projectId, key]);
    return { rows, reload };
}
